import classNames from 'classnames';
import USStates from 'public/assets/maps/USStates.json';
import React, {
  HTMLAttributes,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from 'react-simple-maps';
import { Tooltip } from 'react-tooltip';
import classes from './index.module.scss';

interface USItemsMapProps<T> extends HTMLAttributes<HTMLDivElement> {
  items: T[];
  coordinatesGetter: (item: T) => {
    longitude: number;
    latitude: number;
  };
  stroke?: string;
  strokeWidth?: number;
  fill?: string;
  cardRenderer?: (item: T) => ReactNode;
  circleClassName?: string;
}

const USItemsMap = <T,>({
  items = [],
  coordinatesGetter,
  className,
  stroke,
  strokeWidth,
  fill,
  cardRenderer,
  circleClassName,
}: USItemsMapProps<T>) => {
  const itemWithCoordinates = useMemo(
    () =>
      items.map((item) => ({
        item,
        coordinates: coordinatesGetter(item),
      })),
    [items]
  );
  const [chosenItem, setChosenItem] = useState<T | undefined>(
    items?.[0] || undefined
  );

  useEffect(() => {
    items.length > 0 && setChosenItem(items?.[0]);
  }, [itemWithCoordinates]);

  return (
    <div className={classNames(classes.container, className)}>
      <ComposableMap
        projection="geoAlbersUsa"
        className={classes.map}
      >
        <Geographies geography={USStates}>
          {({ geographies }) => (
            <>
              {geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  stroke={stroke ? stroke : 'var(--color-white)'}
                  strokeWidth={strokeWidth ? strokeWidth : 1.2}
                  geography={geo}
                  fill={fill ? fill : 'var(--color-black)'}
                />
              ))}
            </>
          )}
        </Geographies>
        {itemWithCoordinates?.map(({ item, coordinates }, index) => {
          return (
            <Marker
              key={index.toString()}
              coordinates={[coordinates.longitude, coordinates.latitude]}
              data-tooltip-id="lend-map-tooltip"
              data-some-relevant-attr={item}
              onClick={() => setChosenItem(item)}
            >
              <circle className={classNames(classes.circle, circleClassName)} />
            </Marker>
          );
        })}
      </ComposableMap>
      {cardRenderer && chosenItem && (
        <Tooltip
          className={classes.tooltip}
          id="lend-map-tooltip"
          openOnClick
          clickable
        >
          {cardRenderer(chosenItem)}
        </Tooltip>
      )}
    </div>
  );
};

export default USItemsMap;
